import { useCallback, useEffect, useRef, useState } from "react";
import epaperService from "../../../apiService/epaper.service";
import {
  EMPTY_PREVIEW_MODAL,
  PDF_SLIDE_MS,
  PDF_SLIDE_SETTLE_MS,
} from "../constants";
import {
  extractUpdateMessage,
  getEditionApiValue,
  getEditionPreview,
} from "../utils";

export function useEpaperUpdates({
  editions,
  isMultiDayRange,
  updateDateApi,
  fetchDashboard,
  waitForDashboardRefresh,
}) {
  const editionsRef = useRef(editions);
  const updateLockRef = useRef(false);
  const slideTimersRef = useRef([]);
  const mountedRef = useRef(true);

  editionsRef.current = editions;

  const [updatingEditionId, setUpdatingEditionId] = useState(null);
  const [updateConflictMessage, setUpdateConflictMessage] = useState("");
  const [isWaitingForDashboard, setIsWaitingForDashboard] = useState(false);
  const [previewModal, setPreviewModal] = useState(EMPTY_PREVIEW_MODAL);
  const [pageSlideDirection, setPageSlideDirection] = useState("next");
  const [isPageSliding, setIsPageSliding] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      slideTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const pollUntilReady = useCallback(
    async (options = {}) => {
      setIsWaitingForDashboard(true);
      try {
        return await waitForDashboardRefresh(options);
      } finally {
        if (mountedRef.current) {
          setIsWaitingForDashboard(false);
        }
      }
    },
    [waitForDashboardRefresh]
  );

  const updateSingleEdition = useCallback(
    async (edition) => {
      await epaperService.updateEdition({
        date: updateDateApi,
        edition: getEditionApiValue(edition),
      });
    },
    [updateDateApi]
  );

  const handleUpdateSingle = useCallback(
    async (editionId) => {
      if (isMultiDayRange || updateLockRef.current) return;
      const edition = editionsRef.current.find((item) => item.id === editionId);
      if (!edition) return;

      updateLockRef.current = true;
      setUpdatingEditionId(editionId);
      setUpdateConflictMessage("");
      try {
        await updateSingleEdition(edition);
        await pollUntilReady({
          waitForEditions: true,
          previousEditions: [edition],
        });
      } catch (error) {
        const conflictMessage = extractUpdateMessage(error);
        if (conflictMessage) {
          setUpdateConflictMessage(conflictMessage);
        }
        await fetchDashboard({ silent: true });
      } finally {
        updateLockRef.current = false;
        if (mountedRef.current) {
          setUpdatingEditionId(null);
        }
      }
    },
    [fetchDashboard, isMultiDayRange, pollUntilReady, updateSingleEdition]
  );

  const openPreview = useCallback((edition) => {
    const preview = getEditionPreview(edition);
    setPreviewModal({
      isOpen: true,
      editionName: edition.name,
      pdfPath: preview.pdfPath,
      previewUrl: preview.previewUrl,
      mapPreview: preview.mapPreview,
      allPagesPdfPaths: preview.allPagesPdfPaths,
      currentPageIndex: 0,
    });
  }, []);

  const closePreview = useCallback(() => {
    slideTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    slideTimersRef.current = [];
    setIsPageSliding(false);
    setPreviewModal(EMPTY_PREVIEW_MODAL);
  }, []);

  const triggerPdfPageSlide = useCallback((direction, nextIndex) => {
    setPageSlideDirection(direction);
    setIsPageSliding(true);
    const slideTimer = window.setTimeout(() => {
      setPreviewModal((prev) => ({ ...prev, currentPageIndex: nextIndex }));
      const settleTimer = window.setTimeout(() => {
        setIsPageSliding(false);
      }, PDF_SLIDE_SETTLE_MS);
      slideTimersRef.current.push(settleTimer);
    }, PDF_SLIDE_MS);
    slideTimersRef.current.push(slideTimer);
  }, []);

  const handlePreviewPrev = useCallback(() => {
    triggerPdfPageSlide("prev", Math.max(0, previewModal.currentPageIndex - 1));
  }, [previewModal.currentPageIndex, triggerPdfPageSlide]);

  const handlePreviewNext = useCallback(() => {
    triggerPdfPageSlide(
      "next",
      Math.min(previewModal.allPagesPdfPaths.length - 1, previewModal.currentPageIndex + 1)
    );
  }, [previewModal.allPagesPdfPaths.length, previewModal.currentPageIndex, triggerPdfPageSlide]);

  const dismissConflictMessage = useCallback(() => {
    setUpdateConflictMessage("");
  }, []);

  return {
    updatingEditionId,
    updateConflictMessage,
    isWaitingForDashboard,
    previewModal,
    pageSlideDirection,
    isPageSliding,
    handleUpdateSingle,
    openPreview,
    closePreview,
    handlePreviewPrev,
    handlePreviewNext,
    dismissConflictMessage,
  };
}
